import { css } from '@emotion/react'
import { Tabs, Theme, Tooltip, useTheme } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { PageContainer } from 'components/layout/PageContainer'
import { PageContent } from 'components/layout/PageContent'
import { LoadingIndicator, PageLoading } from 'components/loading'
import { PecSwitch, PrivateRoute, TabLink } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import { Lotacao } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useMemo } from 'react'
import { Redirect, useLocation } from 'react-router'
import { useRouteMatch } from 'react-router-dom'
import Permissions from 'types/Permissions.generated'
import { emptyArrayOf } from 'util/array'
import { calculateAge } from 'util/date/calculateAge'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { isEmpty } from 'util/validation/Util'
import { AgendamentosCidadaoView } from 'view/agenda/cidadao/AgendamentosCidadaoView'
import { ACOMPANHAMENTOS_URL, AcompanhamentosView } from 'view/atendimentos/detail/acompanhamentos/AcompanhamentosView'
import { useAcompanhamentoPuerperio } from 'view/atendimentos/detail/acompanhamentos/puerperio/hooks/useAcompanhamentoPuerperio'
import { FolhaRostoView } from 'view/atendimentos/detail/folha-rosto/FolhaRostoView'
import { CidadaoFolhaRosto } from 'view/atendimentos/detail/folha-rosto/model'
import { ProntuarioFamiliarView } from 'view/atendimentos/detail/prontuario-familiar/ProntuarioFamiliarView'
import { usePeriodoGestacional } from 'view/atendimentos/detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { EncaminhamentoExternoTable } from 'view/atendimentos/detail/soap/plano/encaminhamento-externo/list/EncaminhamentoExternoTable'
import { grupoCboAcompanhamentoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/acessos'
import { VacinacaoReadOnlyView } from 'view/atendimentos/detail/vacinacao/VacinacaoView'
import { LotacaoAtendimento } from 'view/atendimentos/types/AtendimentoProfissionalModel'

import { PeriodoGestacaoModel } from '../../../atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { HistoricoAtendimentoView } from '../../../atendimentos/detail/historico/HistoricoAtendimentoView'
import {
  grupoCboVisualizarAcompanhamentos,
  grupoCboVisualizarApenasEncaminhamentos,
  grupoCboVisualizarProntuario,
  grupoCboVisualizarVacinacao,
} from '../../authorization/acessos'
import {
  CidadaoTabSectionModel,
  visualizarAgendaAndProntuarioPermissions,
  visualizarCidadaoAndProntuarioPermissions,
} from '../types/model-cidadaoTabSection'
import { CidadaoInformacoesView } from './CidadaoInformacoesView'
import { CidadaoUnificacoesView } from './CidadaoUnificacoesView'

export interface CidadaoTabSectionProps {
  cidadao: CidadaoTabSectionModel
}

export const CidadaoTabSection = (props: CidadaoTabSectionProps) => {
  const { cidadao } = props
  const { analytics } = useAnalytics()
  const match = useRouteMatch()
  const location = useLocation()
  const theme = useTheme()
  const { PILOTO_PRONTUARIO_FAMILIAR_ENABLED } = useFlags()
  const classes = createStyles(theme)

  const { acesso, profissional, hasCboAuth, hasAuthorization } = useAcessoLotacaoOrEstagio({
    fetchPolicy: 'cache-only',
  })

  const { getServerTimeNow } = useServerTime()

  const now = getServerTimeNow()
  const dataReferencia = dateAsYyyyMmDd(now)

  const idadeEmAnos = cidadao?.dataNascimento && calculateAge(cidadao.dataNascimento, now.valueOf()).years

  const cidadaoFolhaRosto: CidadaoFolhaRosto = { ...cidadao, idadeEmAnos }

  const lotacao: LotacaoAtendimento = useMemo(() => ({ ...(acesso as Lotacao), profissional }), [acesso, profissional])

  const hasProntuario = !!cidadao.prontuario
  const tooltip = !hasProntuario && 'Cidadão não possui prontuário'
  const prontuarioId = cidadao?.prontuario?.id
  const preNatalAtivo = cidadao?.prontuario?.preNatalAtivo
  const ultimaDumPreNatalAtivo = preNatalAtivo?.ultimaDum
  const isCidadaoVivo = !cidadao?.faleceu

  const { data: puerperio, loading: loadingPuerperio } = useAcompanhamentoPuerperio(prontuarioId, now.getTime())
  const isGestante = !isEmpty(cidadao.prontuario?.preNatalAtivo)
  const hasAcompanhamentoPreNatal = isGestante
  const hasAcompanhamentoPuerperio = !loadingPuerperio && !!puerperio?.acompanhamentoAtivo
  const isPuerpera = !loadingPuerperio && !!puerperio?.puerperioAtivo

  const hasAccessToAcompanhamentos =
    hasCboAuth(grupoCboVisualizarAcompanhamentos) && hasAuthorization(Permissions.visualizarProntuario)

  const hasToRenderAcompanhamentos =
    hasAccessToAcompanhamentos && (hasAcompanhamentoPreNatal || hasAcompanhamentoPuerperio)
  const isAcompanhamentosRoute = location.pathname.startsWith(`${match.url}/${ACOMPANHAMENTOS_URL}`)
  const hasToRenderAcompanhamentosRoute =
    hasToRenderAcompanhamentos || (hasAccessToAcompanhamentos && isAcompanhamentosRoute)

  const { idadeGestacional, loading } = usePeriodoGestacional({
    prontuarioId,
    dum: ultimaDumPreNatalAtivo,
    dataAtendimento: now,
    skip: !hasCboAuth(grupoCboAcompanhamentoPreNatal),
  })

  if (loading) {
    return <PageLoading message='Carregando atendimento...' />
  }

  const renderInformacoes = () => (
    <PageContent fluid type='filled'>
      <CidadaoInformacoesView cidadaoId={cidadao.id} />
    </PageContent>
  )

  const renderUnificacoes = () => (
    <PageContent fluid type='filled'>
      <CidadaoUnificacoesView idCidadao={cidadao.id} temUnificacoes={cidadao.unificado} />
    </PageContent>
  )

  const renderFolhaRosto = () => (
    <FolhaRostoView
      prontuarioId={prontuarioId}
      cidadao={cidadaoFolhaRosto}
      lotacao={lotacao}
      isGestante={isGestante}
      isPuerpera={isPuerpera}
      gestacoes={cidadao.prontuario?.gestacoes || emptyArrayOf<PeriodoGestacaoModel>()}
      idadeGestacional={idadeGestacional}
      isAtendimentoOdonto={false}
      isAtendimentoProcedimentos={false}
    />
  )

  const renderEncaminhamentos = () => (
    <PageContent fluid type='filled'>
      <EncaminhamentoExternoTable
        prontuarioId={cidadao.prontuario.id}
        dataReferencia={Number(dataReferencia)}
        lotacao={lotacao}
        readOnly
      />
    </PageContent>
  )

  const renderHistorico = () => (
    <HistoricoAtendimentoView
      prontuarioId={prontuarioId}
      cidadao={cidadao}
      unidadeSaudeCnes={acesso?.unidadeSaude.cnes}
      isAtendimentoObservacao={false}
      isObservacaoAndResponsavel={false}
    />
  )

  const renderAgendamentos = () => (
    <PageContent fluid type='filled'>
      <AgendamentosCidadaoView />
    </PageContent>
  )

  const renderVacinacao = () => <VacinacaoReadOnlyView dataReferencia={Number(getServerTimeNow())} cidadao={cidadao} />

  const renderAcompanhamentos = () => {
    if (loadingPuerperio && !hasAcompanhamentoPreNatal) {
      return <LoadingIndicator />
    }

    if (hasAcompanhamentoPreNatal || hasAcompanhamentoPuerperio) {
      return (
        <AcompanhamentosView
          cidadaoId={cidadao?.id}
          prontuarioId={prontuarioId}
          isGestante={isGestante}
          isPuerpera={hasAcompanhamentoPuerperio}
          dataReferencia={now.getTime()}
          isVisualizacaoProntuario
        />
      )
    }

    return <Redirect to={`${match.url}/informacoes${location.search}`} />
  }

  const renderProntuarioFamiliar = () => <ProntuarioFamiliarView cidadaoId={cidadao.id} />

  return (
    <Fragment>
      <div css={classes.container}>
        <PageContainer fluid>
          <Tabs style={classes.tabs} data-testid='CidadaoHeaderTab'>
            <TabLink to={`${match.url}/informacoes${location.search}`}>Informações</TabLink>
            {PILOTO_PRONTUARIO_FAMILIAR_ENABLED && isCidadaoVivo && (
              <CheckPermission permission={Permissions.gestaoDeCadastros.visualizarCadastroIndividual.cadastrarEEditar}>
                <TabLink to={`${match.url}/prontuario-familiar${location.search}`}>Prontuário Familiar</TabLink>
              </CheckPermission>
            )}

            <CheckPermission
              hasAnyCbo={[grupoCboVisualizarProntuario]}
              hasAnyPermission={[Permissions.visualizarProntuario]}
            >
              <Tooltip text={tooltip}>
                <TabLink to={`${match.url}/folha-rosto${location.search}`} disabled={!hasProntuario}>
                  Folha de rosto
                </TabLink>
              </Tooltip>
            </CheckPermission>

            {hasToRenderAcompanhamentos && (
              <CheckPermission
                hasAnyCbo={[grupoCboVisualizarAcompanhamentos]}
                hasAnyPermission={[Permissions.visualizarProntuario]}
              >
                <Tooltip text={tooltip}>
                  <TabLink
                    to={`${match.url}/${ACOMPANHAMENTOS_URL}${location.search}`}
                    disabled={!hasProntuario}
                    active={isAcompanhamentosRoute}
                  >
                    Acompanhamentos
                  </TabLink>
                </Tooltip>
              </CheckPermission>
            )}

            <CheckPermission
              hasAnyCbo={[grupoCboVisualizarApenasEncaminhamentos]}
              hasAnyPermission={[Permissions.visualizarProntuario]}
            >
              <Tooltip text={tooltip}>
                <TabLink to={`${match.url}/encaminhamentos${location.search}`} disabled={!hasProntuario}>
                  Encaminhamentos
                </TabLink>
              </Tooltip>
            </CheckPermission>

            <CheckPermission
              hasAnyCbo={[grupoCboVisualizarProntuario]}
              hasAnyPermission={[Permissions.visualizarProntuario]}
            >
              <Tooltip text={tooltip}>
                <TabLink
                  to={`${match.url}/historico${location.search}`}
                  onClick={() => analytics.logEvent('click_tab_cidadao_historico')}
                  disabled={!hasProntuario}
                >
                  Histórico
                </TabLink>
              </Tooltip>
            </CheckPermission>

            <CheckPermission
              hasAnyCbo={[grupoCboVisualizarVacinacao]}
              hasAnyPermission={[Permissions.visualizarProntuario]}
            >
              <Tooltip text={tooltip}>
                <TabLink to={`${match.url}/vacinacao${location.search}`} disabled={!hasProntuario}>
                  Vacinação
                </TabLink>
              </Tooltip>
            </CheckPermission>

            <TabLink to={`${match.url}/unificacoes${location.search}`}>Unificações</TabLink>
            <CheckPermission permission={visualizarAgendaAndProntuarioPermissions}>
              <TabLink to={`${match.url}/agendamentos${location.search}`}>Agendamentos</TabLink>
            </CheckPermission>
          </Tabs>
        </PageContainer>
      </div>

      <PecSwitch>
        <Redirect exact path={`${match.url}`} to={`${match.url}/informacoes${location.search}`} />
        {hasProntuario && (
          <PrivateRoute
            path={`${match.url}/folha-rosto`}
            render={renderFolhaRosto}
            permission={Permissions.visualizarProntuario}
            cbos={grupoCboVisualizarProntuario}
          />
        )}
        {hasProntuario && (
          <PrivateRoute
            path={`${match.url}/encaminhamentos`}
            render={renderEncaminhamentos}
            permission={Permissions.visualizarProntuario}
            cbos={grupoCboVisualizarApenasEncaminhamentos}
          />
        )}
        {hasProntuario && (
          <PrivateRoute
            path={`${match.url}/encaminhamentos`}
            render={renderEncaminhamentos}
            permission={Permissions.visualizarProntuario}
            cbos={grupoCboVisualizarApenasEncaminhamentos}
          />
        )}
        {hasProntuario && (
          <PrivateRoute
            exact
            path={`${match.url}/historico`}
            render={renderHistorico}
            permission={grupoCboVisualizarProntuario}
            cbos={grupoCboVisualizarProntuario}
          />
        )}
        {hasProntuario && (
          <PrivateRoute
            exact
            path={`${match.path}/vacinacao`}
            render={renderVacinacao}
            permission={Permissions.visualizarProntuario}
            cbos={grupoCboVisualizarVacinacao}
          />
        )}
        {hasProntuario && hasToRenderAcompanhamentosRoute && (
          <PrivateRoute
            path={`${match.url}/${ACOMPANHAMENTOS_URL}`}
            render={renderAcompanhamentos}
            permission={Permissions.visualizarProntuario}
            cbos={grupoCboVisualizarAcompanhamentos}
          />
        )}
        <PrivateRoute
          exact
          path={`${match.url}/informacoes`}
          render={renderInformacoes}
          permission={visualizarCidadaoAndProntuarioPermissions}
        />
        <PrivateRoute
          path={`${match.url}/informacoes${location.search}`}
          permission={visualizarCidadaoAndProntuarioPermissions}
        />

        <PrivateRoute
          exact
          path={`${match.url}/unificacoes`}
          render={renderUnificacoes}
          permission={visualizarCidadaoAndProntuarioPermissions}
        />
        <PrivateRoute
          exact
          path={`${match.path}/agendamentos`}
          render={renderAgendamentos}
          permission={visualizarAgendaAndProntuarioPermissions}
        />
        {PILOTO_PRONTUARIO_FAMILIAR_ENABLED && isCidadaoVivo && (
          <PrivateRoute
            exact
            path={`${match.path}/prontuario-familiar`}
            render={renderProntuarioFamiliar}
            permission={visualizarCidadaoAndProntuarioPermissions}
          />
        )}
      </PecSwitch>
    </Fragment>
  )
}

const createStyles = (theme: Theme) => ({
  container: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  tabs: css`
    margin-top: 0.5rem;
    border-bottom-width: 0;
  `,
})
